import React, { useState, useEffect } from 'react';
import { Button, Flex, IconButton, Skeleton, Text, Box } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { PropTypes } from 'prop-types';
import { IoEyeSharp } from 'react-icons/io5';
import { Tooltip } from '@/components/ui/tooltip';
import { previewFromS3Link } from '../../../helpers/downloads';
import { downloadFile } from '../../../utils';
import { DialogBody, DialogContent, DialogHeader, DialogRoot, DialogCloseTrigger } from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';

const ViewDocument = ({ url }) => {
  const [openViewModal, setOpenViewModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewURL, setPreviewURL] = useState('');
  const [fileName, setFileName] = useState('document');

  const getPreviewUrl = async () => {
    try {
      setIsLoading(true);
      const fName = url?.split('/').pop() || 'document';
      setFileName(fName);
      const signedUrl = await previewFromS3Link(url);
      if (!signedUrl) throw new Error('Failed to get preview URL');

      setPreviewURL(signedUrl);
      setIsLoading(false);
    } catch (error) {
      toaster.create({
        title: 'Could not load preview',
        description: 'The file will open in a new tab instead.',
        status: 'error',
      });
      setIsLoading(false);
    }
  };

  const downloadFileHandler = async () => {
    setDownloadLoading(true);
    try {
      const fName = fileName;
      const signedUrl = await previewFromS3Link(url);
      await downloadFile(signedUrl, fName);
    } catch (err) {
      toaster.create({ title: 'Download failed', status: 'error' });
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (openViewModal) getPreviewUrl();
  }, [openViewModal]);

  const renderPreview = () => {
    if (isLoading) {
      return (
        <Flex h="100%" w="100%" justify="center" align="center" bg="gray.50">
          <Skeleton w="100%" h="100%" />
        </Flex>
      );
    }

    const ext = fileName.split('.').pop()?.toLowerCase();

    // Microsoft Office Viewer (best for .docx, .xlsx, .pptx)
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewURL)}`}
          width="100%"
          height="100%"
          frameBorder="0"
          title="Office Document"
        />
      );
    }

    // Google Docs Viewer for PDF, images, and everything else
    return (
      <iframe
        src={`https://docs.google.com/gview?url=${encodeURIComponent(previewURL)}&embedded=true`}
        width="100%"
        height="100%"
        style={{ border: 'none' }}
        title="Document Preview"
        allowFullScreen
      />
    );
  };

  return (
    <>
      <Tooltip content="View">
        <IconButton
          width="24px"
          height="24px"
          padding="0"
          minW="auto"
          border="1px solid #D9DADF"
          borderRadius="2px"
          color="#00000099"
          backgroundColor="#fff"
          onClick={() => setOpenViewModal(true)}
        >
          <IoEyeSharp size={12} />
        </IconButton>
      </Tooltip>
      {openViewModal && (
        <DialogRoot open={openViewModal} onClose={() => setOpenViewModal(false)}>
          <DialogContent
              w="90vw"
              maxW="95vw"
              h="calc(100vh - 100px)"
              maxH="95vh"
              overflow="hidden"
            >
            <DialogHeader>
              <Flex justify="space-between" align="center">
                <Flex align="center" gap={3}>
                  <IconButton 
                  marginRight="10px"
                  background="transparent"
                  _hover={{ background: 'transparent' }}
                  onClick={() => setOpenViewModal(false)} 
                  >
                    <FiArrowLeft size="20px" color='#000'/>
                  </IconButton>
                  <Text>{decodeURI(fileName)}</Text>
                </Flex>
                <Flex justifyContent="center" align="center" gap={3} css={{ gap: '10px' }}>
                  <Button
                  background="#031B4E"
                  _hover={{ background: '#031B4E' }}
                    _focus={{ background: '#031B4E' }}
                    onClick={downloadFileHandler}
                    isLoading={downloadLoading}
                  >
                    Download
                  </Button>
                  <DialogCloseTrigger asChild onClick={() => setOpenViewModal(false)} pos="static" />
                </Flex>
              </Flex>
            </DialogHeader>

            <DialogBody p={0} h="calc(100vh - 140px)">
              {renderPreview()}
            </DialogBody>
          </DialogContent>
        </DialogRoot>
      )}
    </>
  );
};

ViewDocument.propTypes = {
  url: PropTypes.string.isRequired,
};

export default ViewDocument;